import { useEffect, useRef, useCallback } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';
import { MytaverseLogger } from '@helpers/logger';
import AvaturnService from '@features/avaturn/services/AvaturnService';
import { getUseAvaturnHelper } from '@features/avatar/helpers';
import { AvaturnStatuses } from '@features/avaturn/interfaces';
import { getCatchErrorMessage } from '@helpers/error';
import { AvatarTypes } from '@interfaces/avatar';
import ParticipantsService from '@services/ParticipantsService';
import { StatusEnum } from '../interfaces';
import useTryOnInit from './useTryOnInit';
import { useChangeAvatarState } from '../context';
import AvaturnLogger from '@features/avaturn/pages/Avaturn/provider/logger';

const useAvaturnHelper = getUseAvaturnHelper();

export const useReloadAvatar = () => {
  const { user, setCustomAvatarUrl } = useMytaverse();

  //Prepare try-on avatar on enter in case active avatar don't have try-on one
  useTryOnInit();

  const {
    avaturnId,
    stateSnapshot,
    status,
    setAvaturnId,
    setStatus,
    setStateSnapshot,
  } = useChangeAvatarState();

  const { getChangeAvatarParams } = useAvaturnHelper();

  const avaturnIdRef = useRef(avaturnId);
  useEffect(() => {
    avaturnIdRef.current = avaturnId;
  }, [avaturnId]);

  const changeAvatar = useCallback(
    async (payload: string) => {
      if (!user.activeAvatarId) {
        AvaturnLogger.log(`changeAvatar no activeAvatarId`);
        return;
      }

      if (avaturnIdRef.current) {
        AvaturnLogger.log(`[TryOn] Avatar was initiated`);
        return;
      }

      if (status !== StatusEnum.Idle) {
        return;
      }

      const param = getChangeAvatarParams(payload);
      if (!param) {
        return;
      }

      try {
        setStatus(StatusEnum.Loading);

        const result = await AvaturnService.changeAvatar({
          ...param,
          avatarId: user.activeAvatarId,
        });

        setAvaturnId(result.avaturnId);
      } catch (e) {
        MytaverseLogger.log(`changeAvatar: ${JSON.stringify(e)}`);
        setStatus(StatusEnum.Idle);
      }
    },
    [status, user, getChangeAvatarParams],
  );

  const fetchAvatarInfo = useCallback(async () => {
    if (!avaturnIdRef.current) {
      setTimeout(fetchAvatarInfo, 5000);
      return;
    }

    setStatus(StatusEnum.Processing);

    try {
      const data = await AvaturnService.getAvatarInfo(avaturnIdRef.current);

      if (
        data?.status &&
        [
          AvaturnStatuses.FAILED_CREATION,
          AvaturnStatuses.FAILED_EXPORTING,
          AvaturnStatuses.FAILED,
        ].includes(data.status)
      ) {
        throw new Error('Cannot create an avatar');
      } else if (data.avatarUrl) {
        setCustomAvatarUrl((prevState) => {
          if (prevState) {
            setStateSnapshot({
              initAvaturnUrl: prevState,
              generatedAvatarId: data.avatarId as string,
            });
          }

          return data.avatarUrl as string;
        });

        await ParticipantsService.addParticipantAvatarToList({
          avatarType: AvatarTypes.AVATURN,
          avatarId: data.avatarId,
        });

        //clean states
        setAvaturnId(null);
        setStatus(StatusEnum.Idle);
      } else {
        setTimeout(fetchAvatarInfo, 5000);
      }
    } catch (e) {
      MytaverseLogger.log(`fetchAvatarInfo: ${getCatchErrorMessage(e)}`);
      setStatus(StatusEnum.Error);
    }
  }, []);

  const reloadAvatar = useCallback(
    async (payload: string) => {
      if ([StatusEnum.Processing, StatusEnum.Error].includes(status)) {
        return;
      }

      fetchAvatarInfo();
    },
    [avaturnId, status, fetchAvatarInfo],
  );

  const reloadInitialAvatar = useCallback(() => {
    if (stateSnapshot) {
      setCustomAvatarUrl(stateSnapshot.initAvaturnUrl);
    }
  }, [getChangeAvatarParams, stateSnapshot]);

  return {
    status,
    changeAvatar,
    reloadAvatar,
    reloadInitialAvatar,
  };
};
